// src/components/AgentObs/AgentObsView.jsx
import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  Bot, Zap, DollarSign, Clock, ShieldCheck, ThumbsUp, ThumbsDown,
  GitBranch, Activity, AlertTriangle, CheckCircle, XCircle,
  Search, Filter, ChevronDown, ChevronRight, Copy, RefreshCw,
  BarChart2, TrendingUp, TrendingDown, Eye, Layers, Hash,
  MessageSquare, Star, FlaskConical, Lock, ShieldAlert, Cpu,
  X, Info, ArrowRight, Fingerprint,
} from 'lucide-react';

// ─── Model registry & pricing ─────────────────────────────────────────────────
const MODELS = {
  'gpt-4o':           { provider:'OpenAI',     color:'#10a37f', inPer1M: 5.00,  outPer1M: 15.00, icon:'🤖' },
  'gpt-4o-mini':      { provider:'OpenAI',     color:'#74aa9c', inPer1M: 0.15,  outPer1M: 0.60,  icon:'🤖' },
  'claude-3-5-sonnet':{ provider:'Anthropic',  color:'#d97706', inPer1M: 3.00,  outPer1M: 15.00, icon:'🧠' },
  'claude-3-haiku':   { provider:'Anthropic',  color:'#f59e0b', inPer1M: 0.25,  outPer1M: 1.25,  icon:'🧠' },
  'gemini-1.5-pro':   { provider:'Google',     color:'#4285f4', inPer1M: 3.50,  outPer1M: 10.50, icon:'✨' },
  'llama-3.1-70b':    { provider:'Self-hosted',color:'#8b5cf6', inPer1M: 0.00,  outPer1M: 0.00,  icon:'🦙' },
};

const MODEL_NAMES = Object.keys(MODELS);

// ─── Demo data generators ─────────────────────────────────────────────────────
const rnd  = (lo, hi)   => Math.random() * (hi - lo) + lo;
const rndI = (lo, hi)   => Math.floor(rnd(lo, hi));
const pick  = arr        => arr[rndI(0, arr.length)];
const uid   = ()         => Math.random().toString(36).slice(2, 10).toUpperCase();

function calcCost(model, inTok, outTok) {
  const m = MODELS[model];
  return ((inTok * m.inPer1M) + (outTok * m.outPer1M)) / 1_000_000;
}

const TASK_TYPES = ['chat', 'completion', 'summarization', 'rag-query', 'function-call', 'embedding', 'code-gen', 'analysis'];
const AGENTS     = ['customer-support-agent', 'code-assistant', 'data-analyst', 'document-qa', 'email-drafter', 'search-agent'];

const SAMPLE_PROMPTS = [
  'Summarize the Q3 earnings report and highlight key financial risks.',
  'Generate a Python function that parses nested JSON with error handling.',
  'What are the compliance requirements for HIPAA data storage in AWS?',
  'Analyze the sentiment of the following customer reviews and categorize them.',
  'Create a detailed incident response plan for a ransomware attack scenario.',
  'Translate the following technical documentation into plain English.',
  'What are the pros and cons of microservices vs monolithic architecture?',
  'Debug this SQL query and suggest performance optimizations.',
  'Draft a professional email declining a vendor proposal.',
  'Explain quantum entanglement to a 12-year-old.',
];

function makeTrace(minutesAgo) {
  const model    = pick(MODEL_NAMES);
  const taskType = pick(TASK_TYPES);
  const agent    = pick(AGENTS);
  const inTok    = rndI(200, 4200);
  const outTok   = rndI(80, 1800);
  const latency  = rndI(300, 8500);
  const cached   = Math.random() < 0.15;
  const status   = Math.random() < 0.05 ? 'error' : Math.random() < 0.03 ? 'timeout' : 'success';
  const cost     = calcCost(model, inTok, outTok);

  // Safety scores (0–1)
  const toxicity      = Math.max(0, rnd(0, 0.18) - (Math.random() < 0.97 ? 0.12 : 0));
  const groundedness  = rnd(0.62, 1.0);
  const relevance     = rnd(0.70, 1.0);
  const faithfulness  = rnd(0.65, 1.0);
  const promptInjection = Math.random() < 0.03;
  const piiDetected   = Math.random() < 0.04;

  // Spans
  const t0 = 0;
  const cacheMs = cached ? rndI(1, 8) : 0;
  const tokenMs = rndI(10, 40);
  const inferMs = cached ? rndI(5, 20) : rndI(latency * 0.6, latency * 0.85);
  const safeMs  = rndI(15, 60);
  const parseMs = rndI(5, 25);
  const spans = [
    { name: 'cache-lookup',        start: t0,                  dur: cacheMs || rndI(1,8), status: cached ? 'cache-hit' : 'cache-miss' },
    { name: 'prompt-tokenization', start: t0 + rndI(1,5),      dur: tokenMs,              status: 'ok' },
    { name: 'llm-inference',       start: t0 + tokenMs + 10,   dur: inferMs,              status: status === 'error' ? 'error' : 'ok' },
    { name: 'safety-check',        start: t0 + tokenMs + inferMs + 12, dur: safeMs,       status: toxicity > 0.12 ? 'flagged' : 'ok' },
    { name: 'response-parse',      start: t0 + tokenMs + inferMs + safeMs + 14, dur: parseMs, status: 'ok' },
  ];

  return {
    id:         uid(),
    model,
    agent,
    taskType,
    prompt:     pick(SAMPLE_PROMPTS),
    inTokens:   inTok,
    outTokens:  outTok,
    totalTokens:inTok + outTok,
    cost,
    latencyMs:  latency,
    cached,
    status,
    toxicity:   parseFloat(toxicity.toFixed(3)),
    groundedness: parseFloat(groundedness.toFixed(3)),
    relevance:  parseFloat(relevance.toFixed(3)),
    faithfulness: parseFloat(faithfulness.toFixed(3)),
    promptInjection,
    piiDetected,
    spans,
    feedback:   Math.random() < 0.35 ? (Math.random() < 0.78 ? 'positive' : 'negative') : null,
    rating:     Math.random() < 0.3 ? rndI(1, 6) : null,
    timestamp:  new Date(Date.now() - minutesAgo * 60000 - rndI(0, 59000)),
  };
}

// Generate 300 traces over last 24 h
const ALL_TRACES = Array.from({ length: 300 }, (_, i) =>
  makeTrace(rnd(0, 1440))
).sort((a, b) => b.timestamp - a.timestamp);

// Hourly buckets for charts (last 24 h)
const HOURLY = Array.from({ length: 24 }, (_, i) => {
  const bucket = ALL_TRACES.filter(t => {
    const h = Math.floor((Date.now() - t.timestamp.getTime()) / 3600000);
    return h === 23 - i;
  });
  const inTok  = bucket.reduce((s, t) => s + t.inTokens, 0);
  const outTok = bucket.reduce((s, t) => s + t.outTokens, 0);
  const cost   = bucket.reduce((s, t) => s + t.cost, 0);
  const avgLat = bucket.length ? Math.round(bucket.reduce((s, t) => s + t.latencyMs, 0) / bucket.length) : 0;
  const d = new Date(Date.now() - (23 - i) * 3600000);
  return {
    hour:    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    inTok, outTok, cost: parseFloat(cost.toFixed(4)),
    requests: bucket.length,
    avgLat,
    errors: bucket.filter(t => t.status !== 'success').length,
  };
});

// Per-model aggregates
const MODEL_STATS = MODEL_NAMES.map(m => {
  const traces = ALL_TRACES.filter(t => t.model === m);
  const inTok  = traces.reduce((s, t) => s + t.inTokens, 0);
  const outTok = traces.reduce((s, t) => s + t.outTokens, 0);
  const cost   = traces.reduce((s, t) => s + t.cost, 0);
  const avgLat = traces.length ? Math.round(traces.reduce((s, t) => s + t.latencyMs, 0) / traces.length) : 0;
  const errRate = traces.length ? (traces.filter(t => t.status !== 'success').length / traces.length * 100) : 0;
  const avgTox = traces.length ? traces.reduce((s, t) => s + t.toxicity, 0) / traces.length : 0;
  const p95   = traces.length ? [...traces].sort((a, b) => a.latencyMs - b.latencyMs)[Math.floor(traces.length * 0.95)]?.latencyMs : 0;
  return { model: m, ...MODELS[m], requests: traces.length, inTok, outTok, totalTokens: inTok + outTok,
    cost, avgLat, p95: p95 || 0, errRate: parseFloat(errRate.toFixed(1)), avgTox: parseFloat(avgTox.toFixed(3)) };
});

// Feedback data
const FEEDBACK_TRACES = ALL_TRACES.filter(t => t.feedback);
const POS_COUNT = FEEDBACK_TRACES.filter(t => t.feedback === 'positive').length;
const NEG_COUNT = FEEDBACK_TRACES.filter(t => t.feedback === 'negative').length;
const FEEDBACK_COMMENTS = [
  { type: 'positive', text: 'Very accurate and concise response to my technical question.' },
  { type: 'positive', text: 'The code suggestion worked perfectly — saved me 30 minutes.' },
  { type: 'negative', text: 'Hallucinated a non-existent API endpoint in the documentation.' },
  { type: 'positive', text: 'Great at summarizing long documents without losing key details.' },
  { type: 'negative', text: 'Response was overly verbose and repeated itself multiple times.' },
  { type: 'positive', text: 'Caught a security vulnerability in my code I had missed.' },
  { type: 'negative', text: 'Did not follow the requested output format (JSON vs plain text).' },
  { type: 'positive', text: 'Excellent multi-step reasoning for the data analysis task.' },
];

// Safety summary
const TOTAL = ALL_TRACES.length;
const TOXIC_COUNT   = ALL_TRACES.filter(t => t.toxicity > 0.1).length;
const PI_COUNT      = ALL_TRACES.filter(t => t.promptInjection).length;
const PII_COUNT     = ALL_TRACES.filter(t => t.piiDetected).length;
const LOW_GROUND    = ALL_TRACES.filter(t => t.groundedness < 0.75).length;

// ─── Small helpers ────────────────────────────────────────────────────────────
function fmtCost(c)  { return c < 0.01 ? `$${(c * 1000).toFixed(3)}m` : `$${c.toFixed(4)}`; }
function fmtMs(ms)   { return ms >= 1000 ? `${(ms/1000).toFixed(2)}s` : `${ms}ms`; }
function fmtK(n)     { return n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n); }
function timeAgo(d)  {
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60)   return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  return `${Math.floor(s/3600)}h ago`;
}

const scoreColor = v => v >= 0.85 ? '#22c55e' : v >= 0.65 ? '#f59e0b' : '#ef4444';
const scoreLabel = v => v >= 0.85 ? 'Good' : v >= 0.65 ? 'Fair' : 'Poor';
const toxColor   = v => v < 0.05 ? '#22c55e' : v < 0.12 ? '#f59e0b' : '#ef4444';
const toxLabel   = v => v < 0.05 ? 'Safe' : v < 0.12 ? 'Review' : 'Flagged';

const STATUS_S = {
  success: { color:'#22c55e', bg:'rgba(34,197,94,.15)',   label:'Success' },
  error:   { color:'#ef4444', bg:'rgba(239,68,68,.15)',   label:'Error'   },
  timeout: { color:'#f59e0b', bg:'rgba(245,158,11,.15)', label:'Timeout' },
};

// ─── Shared UI ────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, color, trend }) {
  return (
    <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderTop:`3px solid ${color}` }}>
      <div className="flex items-center gap-1.5 text-xs mb-2" style={{ color:'var(--text-3)' }}>
        <Icon size={11} style={{ color }} />{label}
      </div>
      <div className="mono text-2xl font-bold mb-1" style={{ color }}>{value}</div>
      <div className="flex items-center gap-1 text-xs" style={{ color:'var(--text-3)' }}>
        {trend !== undefined && (trend >= 0
          ? <TrendingUp size={10} style={{ color:'#22c55e' }} />
          : <TrendingDown size={10} style={{ color:'#ef4444' }} />
        )}
        {sub}
      </div>
    </div>
  );
}

function ScorePill({ value, colorFn, labelFn, size='sm' }) {
  const c = colorFn(value);
  const pct = Math.round(value * 100);
  return (
    <span style={{ fontSize: size==='xs' ? 9 : 11, fontWeight:700, padding: size==='xs' ? '1px 6px' : '2px 9px',
      borderRadius:20, background:`${c}18`, color:c, border:`1px solid ${c}30` }}>
      {pct}% {labelFn(value)}
    </span>
  );
}

function ScoreBar({ label, value, colorFn }) {
  const c = colorFn(value);
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span style={{ color:'var(--text-2)' }}>{label}</span>
        <span style={{ color:c, fontWeight:700, fontFamily:'monospace' }}>{(value*100).toFixed(1)}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background:'var(--bg-primary)' }}>
        <div style={{ width:`${value*100}%`, height:'100%', background:c, borderRadius:99, transition:'width .5s' }}/>
      </div>
    </div>
  );
}

// ─── Trace Detail Panel ───────────────────────────────────────────────────────
function TracePanel({ trace, onClose }) {
  const m = MODELS[trace.model];
  const [activeTab, setActiveTab] = useState('overview');
  const maxSpanDur = Math.max(...trace.spans.map(s => s.start + s.dur));
  const spanColors = { ok:'#22c55e', error:'#ef4444', flagged:'#f59e0b', 'cache-hit':'#3b82f6', 'cache-miss':'#64748b' };

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background:'rgba(0,0,0,.55)', backdropFilter:'blur(3px)' }}
      onClick={onClose}>
      <div className="ml-auto h-full w-full overflow-y-auto flex flex-col"
        style={{ maxWidth:560, background:'var(--bg-secondary)', borderLeft:'1px solid var(--border)', boxShadow:'-20px 0 60px rgba(0,0,0,.5)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 shrink-0"
          style={{ borderBottom:'1px solid var(--border)', background:`${m.color}08` }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
            style={{ background:`${m.color}18`, border:`1px solid ${m.color}30` }}>{m.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm">{trace.model}</span>
              <StatusBadge s={trace.status} />
              {trace.cached && <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:20, background:'rgba(59,130,246,.15)', color:'#60a5fa', border:'1px solid rgba(59,130,246,.3)' }}>CACHED</span>}
            </div>
            <div className="text-xs mt-0.5 flex items-center gap-2" style={{ color:'var(--text-3)' }}>
              <span className="mono">{trace.id}</span>
              <span>·</span><span>{trace.agent}</span>
              <span>·</span><span>{timeAgo(trace.timestamp)}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-3)', cursor:'pointer', lineHeight:0 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
          {['overview','spans','safety','prompt'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ padding:'8px 16px', fontSize:11, fontWeight:activeTab===t?600:400,
                color:activeTab===t?'var(--text)':'var(--text-3)', background:'none', border:'none', cursor:'pointer',
                borderBottom:activeTab===t?`2px solid ${m.color}`:'2px solid transparent', textTransform:'capitalize' }}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5 flex flex-col gap-4 flex-1 overflow-y-auto">
          {/* Overview tab */}
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label:'Input Tokens',   value:fmtK(trace.inTokens),  color:'#60a5fa' },
                  { label:'Output Tokens',  value:fmtK(trace.outTokens), color:'#a78bfa' },
                  { label:'Total Cost',     value:fmtCost(trace.cost),   color:'#4ade80' },
                  { label:'Latency',        value:fmtMs(trace.latencyMs),color:trace.latencyMs>5000?'#f87171':'#fbbf24' },
                  { label:'Task Type',      value:trace.taskType,        color:m.color },
                  { label:'Provider',       value:m.provider,            color:m.color },
                ].map(k => (
                  <div key={k.label} className="rounded-lg p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                    <div className="text-xs mb-1" style={{ color:'var(--text-3)' }}>{k.label}</div>
                    <div className="text-sm font-bold mono" style={{ color:k.color }}>{k.value}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-4" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>Quality Scores</div>
                <ScoreBar label="Groundedness" value={trace.groundedness} colorFn={scoreColor} />
                <ScoreBar label="Relevance"    value={trace.relevance}    colorFn={scoreColor} />
                <ScoreBar label="Faithfulness" value={trace.faithfulness} colorFn={scoreColor} />
                <ScoreBar label="Toxicity (lower = better)" value={1 - trace.toxicity} colorFn={v => toxColor(1-v)} />
              </div>
              {(trace.promptInjection || trace.piiDetected) && (
                <div className="rounded-lg p-3 flex items-start gap-2"
                  style={{ background:'rgba(239,68,68,.08)', border:'1px solid rgba(239,68,68,.25)' }}>
                  <ShieldAlert size={13} style={{ color:'#f87171', marginTop:1, flexShrink:0 }} />
                  <div className="text-xs" style={{ color:'#fca5a5' }}>
                    {trace.promptInjection && <div>⚠️ Prompt injection attempt detected</div>}
                    {trace.piiDetected     && <div>⚠️ PII detected in prompt (masked in storage)</div>}
                  </div>
                </div>
              )}
              {trace.feedback && (
                <div className="rounded-lg p-3 flex items-center gap-2"
                  style={{ background: trace.feedback==='positive'?'rgba(34,197,94,.08)':'rgba(239,68,68,.08)', border:`1px solid ${trace.feedback==='positive'?'rgba(34,197,94,.25)':'rgba(239,68,68,.25)'}` }}>
                  {trace.feedback==='positive' ? <ThumbsUp size={13} style={{ color:'#4ade80' }}/> : <ThumbsDown size={13} style={{ color:'#f87171' }}/>}
                  <span className="text-xs font-semibold" style={{ color:trace.feedback==='positive'?'#4ade80':'#f87171' }}>
                    User rated this {trace.feedback}
                  </span>
                  {trace.rating && <span className="ml-auto text-xs" style={{ color:'var(--text-3)' }}>{'★'.repeat(trace.rating)}{'☆'.repeat(5-trace.rating)}</span>}
                </div>
              )}
            </>
          )}

          {/* Spans tab */}
          {activeTab === 'spans' && (
            <div>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>
                Trace Waterfall — {fmtMs(trace.latencyMs)} total
              </div>
              <div className="space-y-2">
                {trace.spans.map((sp, i) => {
                  const pct  = (sp.dur / maxSpanDur) * 100;
                  const left = (sp.start / maxSpanDur) * 100;
                  const c    = spanColors[sp.status] || '#64748b';
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="mono" style={{ color:'var(--text-2)' }}>{sp.name}</span>
                        <div className="flex items-center gap-2">
                          <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:20, background:`${c}18`, color:c, border:`1px solid ${c}25` }}>{sp.status}</span>
                          <span className="mono" style={{ color:'var(--text-3)' }}>{sp.dur}ms</span>
                        </div>
                      </div>
                      <div className="h-5 rounded" style={{ background:'var(--bg-primary)', position:'relative' }}>
                        <div style={{ position:'absolute', left:`${left}%`, width:`${Math.max(pct,1)}%`, height:'100%', background:`${c}80`, borderRadius:4, border:`1px solid ${c}50` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4" style={{ borderTop:'1px solid var(--border)' }}>
                <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>Token Breakdown</div>
                <div className="flex gap-3">
                  <div className="flex-1 rounded-lg p-3 text-center" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                    <div className="text-xs mb-1" style={{ color:'var(--text-3)' }}>Input</div>
                    <div className="mono font-bold text-lg" style={{ color:'#60a5fa' }}>{fmtK(trace.inTokens)}</div>
                    <div className="text-xs" style={{ color:'var(--text-3)' }}>${(trace.inTokens * MODELS[trace.model].inPer1M / 1e6).toFixed(5)}</div>
                  </div>
                  <div className="flex-1 rounded-lg p-3 text-center" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                    <div className="text-xs mb-1" style={{ color:'var(--text-3)' }}>Output</div>
                    <div className="mono font-bold text-lg" style={{ color:'#a78bfa' }}>{fmtK(trace.outTokens)}</div>
                    <div className="text-xs" style={{ color:'var(--text-3)' }}>${(trace.outTokens * MODELS[trace.model].outPer1M / 1e6).toFixed(5)}</div>
                  </div>
                  <div className="flex-1 rounded-lg p-3 text-center" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                    <div className="text-xs mb-1" style={{ color:'var(--text-3)' }}>Total Cost</div>
                    <div className="mono font-bold text-lg" style={{ color:'#4ade80' }}>{fmtCost(trace.cost)}</div>
                    <div className="text-xs" style={{ color:'var(--text-3)' }}>all tokens</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Safety tab */}
          {activeTab === 'safety' && (
            <div className="space-y-4">
              {[
                { label:'Toxicity Score',          value:trace.toxicity,       note:'Lower is better', inv:true },
                { label:'Groundedness',             value:trace.groundedness,   note:'Factual accuracy vs sources' },
                { label:'Relevance',                value:trace.relevance,      note:'Response relevance to prompt' },
                { label:'Faithfulness',             value:trace.faithfulness,   note:'Stays within provided context' },
              ].map(s => {
                const displayVal = s.inv ? 1 - s.value : s.value;
                const c = s.inv ? toxColor(s.value) : scoreColor(s.value);
                return (
                  <div key={s.label} className="rounded-xl p-4" style={{ background:'var(--bg-tertiary)', border:`1px solid ${c}25` }}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="text-sm font-semibold">{s.label}</div>
                        <div className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{s.note}</div>
                      </div>
                      <div className="text-2xl mono font-bold" style={{ color:c }}>{(displayVal*100).toFixed(1)}%</div>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background:'var(--bg-primary)' }}>
                      <div style={{ width:`${displayVal*100}%`, height:'100%', background:c, borderRadius:99 }}/>
                    </div>
                  </div>
                );
              })}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg p-3" style={{ background: trace.promptInjection?'rgba(239,68,68,.1)':'rgba(34,197,94,.08)', border:`1px solid ${trace.promptInjection?'rgba(239,68,68,.3)':'rgba(34,197,94,.2)'}` }}>
                  <div className="flex items-center gap-2 mb-1">
                    {trace.promptInjection ? <ShieldAlert size={12} style={{ color:'#f87171' }}/> : <ShieldCheck size={12} style={{ color:'#4ade80' }}/>}
                    <span className="text-xs font-semibold" style={{ color:trace.promptInjection?'#f87171':'#4ade80' }}>Prompt Injection</span>
                  </div>
                  <div className="text-xs" style={{ color:'var(--text-3)' }}>{trace.promptInjection ? 'Attack detected' : 'No attack detected'}</div>
                </div>
                <div className="rounded-lg p-3" style={{ background:trace.piiDetected?'rgba(245,158,11,.1)':'rgba(34,197,94,.08)', border:`1px solid ${trace.piiDetected?'rgba(245,158,11,.3)':'rgba(34,197,94,.2)'}` }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Fingerprint size={12} style={{ color:trace.piiDetected?'#fbbf24':'#4ade80' }}/>
                    <span className="text-xs font-semibold" style={{ color:trace.piiDetected?'#fbbf24':'#4ade80' }}>PII Detection</span>
                  </div>
                  <div className="text-xs" style={{ color:'var(--text-3)' }}>{trace.piiDetected ? 'PII found & masked' : 'No PII detected'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Prompt tab */}
          {activeTab === 'prompt' && (
            <div className="space-y-3">
              <div className="rounded-xl p-4" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold" style={{ color:'#60a5fa' }}>User Prompt</span>
                  <span className="text-xs mono" style={{ color:'var(--text-3)' }}>{fmtK(trace.inTokens)} tokens</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color:'var(--text-2)' }}>{trace.prompt}</p>
              </div>
              <div className="rounded-xl p-4" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold" style={{ color:'#a78bfa' }}>Model Response</span>
                  <span className="text-xs mono" style={{ color:'var(--text-3)' }}>{fmtK(trace.outTokens)} tokens</span>
                </div>
                <p className="text-sm leading-relaxed italic" style={{ color:'var(--text-3)' }}>
                  [Response content stored securely — click "Decrypt & View" to load with audit log]
                </p>
                <button className="mt-2 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                  style={{ background:'rgba(139,92,246,.15)', border:'1px solid rgba(139,92,246,.3)', color:'#c4b5fd', cursor:'pointer' }}>
                  <Lock size={10}/> Decrypt & View (requires audit permission)
                </button>
              </div>
              <div className="rounded-lg p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                <div className="text-xs font-semibold mb-2" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.8px' }}>Metadata</div>
                {[
                  ['Trace ID',    trace.id],
                  ['Agent',       trace.agent],
                  ['Task Type',   trace.taskType],
                  ['Model',       trace.model],
                  ['Provider',    m.provider],
                  ['Timestamp',   trace.timestamp.toLocaleString()],
                ].map(([k,v]) => (
                  <div key={k} className="flex items-center justify-between py-1 text-xs" style={{ borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                    <span style={{ color:'var(--text-3)' }}>{k}</span>
                    <span className="mono" style={{ color:'var(--text-2)' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const st = STATUS_S[s] || STATUS_S.success;
  return <span style={{ fontSize:9, fontWeight:700, padding:'1px 7px', borderRadius:20, background:st.bg, color:st.color, border:`1px solid ${st.color}30` }}>{st.label}</span>;
}

// ─── Main View ────────────────────────────────────────────────────────────────
const TABS = ['Overview', 'Traces', 'Models', 'Safety', 'Feedback'];

export default function AgentObsView() {
  const [tab,        setTab]        = useState('Overview');
  const [selTrace,   setSelTrace]   = useState(null);
  const [traceSearch,setSearch]     = useState('');
  const [modelFilter,setModelFilter]= useState('ALL');
  const [statusFilter,setStatusFilter] = useState('ALL');
  const [expandModel, setExpandModel] = useState(null);

  // Aggregates
  const totalTokens  = ALL_TRACES.reduce((s,t) => s + t.totalTokens, 0);
  const totalCost    = ALL_TRACES.reduce((s,t) => s + t.cost, 0);
  const totalReqs    = ALL_TRACES.length;
  const avgLatency   = Math.round(ALL_TRACES.reduce((s,t) => s + t.latencyMs, 0) / totalReqs);
  const successRate  = (ALL_TRACES.filter(t => t.status === 'success').length / totalReqs * 100).toFixed(1);
  const cacheHitRate = (ALL_TRACES.filter(t => t.cached).length / totalReqs * 100).toFixed(1);

  const filteredTraces = useMemo(() => ALL_TRACES.filter(t => {
    if (modelFilter !== 'ALL'  && t.model !== modelFilter)  return false;
    if (statusFilter !== 'ALL' && t.status !== statusFilter) return false;
    if (traceSearch && !t.id.includes(traceSearch.toUpperCase()) && !t.agent.includes(traceSearch) && !t.taskType.includes(traceSearch)) return false;
    return true;
  }), [modelFilter, statusFilter, traceSearch]);

  // Pie data for cost by model
  const costPieData = MODEL_STATS.filter(m => m.cost > 0).map(m => ({
    name: m.model, value: parseFloat(m.cost.toFixed(4)), color: m.color,
  }));

  // Daily feedback trend (last 7 days)
  const feedbackTrend = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(Date.now() - (6-i) * 86400000);
    const day = d.toLocaleDateString([], { weekday:'short' });
    const bucket = FEEDBACK_TRACES.filter(t => {
      const td = new Date(t.timestamp);
      return td.toDateString() === d.toDateString();
    });
    return { day, pos: bucket.filter(t => t.feedback==='positive').length, neg: bucket.filter(t => t.feedback==='negative').length };
  });

  // Toxicity histogram
  const toxBuckets = [
    { range:'0–2%',   count: ALL_TRACES.filter(t => t.toxicity < 0.02).length },
    { range:'2–5%',   count: ALL_TRACES.filter(t => t.toxicity >= 0.02 && t.toxicity < 0.05).length },
    { range:'5–10%',  count: ALL_TRACES.filter(t => t.toxicity >= 0.05 && t.toxicity < 0.10).length },
    { range:'10–15%', count: ALL_TRACES.filter(t => t.toxicity >= 0.10 && t.toxicity < 0.15).length },
    { range:'>15%',   count: ALL_TRACES.filter(t => t.toxicity >= 0.15).length },
  ];

  const groundBuckets = [
    { range:'<65%',   count: ALL_TRACES.filter(t => t.groundedness < 0.65).length },
    { range:'65–75%', count: ALL_TRACES.filter(t => t.groundedness >= 0.65 && t.groundedness < 0.75).length },
    { range:'75–85%', count: ALL_TRACES.filter(t => t.groundedness >= 0.75 && t.groundedness < 0.85).length },
    { range:'85–95%', count: ALL_TRACES.filter(t => t.groundedness >= 0.85 && t.groundedness < 0.95).length },
    { range:'>95%',   count: ALL_TRACES.filter(t => t.groundedness >= 0.95).length },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 shrink-0 flex items-center gap-3 flex-wrap"
        style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background:'linear-gradient(135deg,#8b5cf6,#3b82f6)', boxShadow:'0 4px 14px rgba(139,92,246,.4)' }}>
          <Bot size={16} className="text-white" />
        </div>
        <div>
          <h1 className="text-base font-bold">Agent Observability</h1>
          <p className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>
            LLM tracing · token economics · safety scoring · feedback · {MODEL_NAMES.length} models
          </p>
        </div>
        <div className="flex items-center gap-2 ml-4 flex-wrap">
          <StatPill label="Requests" value={fmtK(totalReqs)} color="#60a5fa"/>
          <StatPill label="Total Cost" value={`$${totalCost.toFixed(2)}`} color="#4ade80"/>
          <StatPill label="Tokens" value={`${(totalTokens/1000).toFixed(1)}k`} color="#a78bfa"/>
          <StatPill label="Avg Latency" value={fmtMs(avgLatency)} color="#fbbf24"/>
          <StatPill label="Success" value={`${successRate}%`} color="#22c55e"/>
        </div>
        <button className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs"
          style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', color:'var(--text-2)', cursor:'pointer' }}>
          <RefreshCw size={11}/> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', background:'var(--bg-secondary)', borderBottom:'1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding:'9px 18px', fontSize:12, fontWeight:tab===t?600:400,
              color:tab===t?'var(--text)':'var(--text-3)', background:'none', border:'none', cursor:'pointer',
              borderBottom:tab===t?'2px solid #8b5cf6':'2px solid transparent' }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Overview ──────────────────────────────────────────────────────────── */}
      {tab === 'Overview' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* KPI row */}
          <div className="grid gap-4" style={{ gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))' }}>
            <KpiCard icon={Hash}       label="Total Requests"   value={fmtK(totalReqs)}             sub="last 24h"          color="#60a5fa"  />
            <KpiCard icon={Zap}        label="Total Tokens"     value={`${(totalTokens/1000).toFixed(1)}k`} sub={`${(totalTokens/totalReqs).toFixed(0)} avg/req`} color="#a78bfa"/>
            <KpiCard icon={DollarSign} label="Total Cost"       value={`$${totalCost.toFixed(3)}`}  sub={`$${(totalCost/totalReqs*1000).toFixed(3)} avg/req`} color="#4ade80"/>
            <KpiCard icon={Clock}      label="Avg Latency"      value={fmtMs(avgLatency)}            sub="p50 response time" color="#fbbf24"  />
            <KpiCard icon={CheckCircle}label="Success Rate"     value={`${successRate}%`}            sub={`${totalReqs - ALL_TRACES.filter(t=>t.status==='success').length} errors`} color="#22c55e"/>
            <KpiCard icon={Activity}   label="Cache Hit Rate"   value={`${cacheHitRate}%`}           sub="prompt cache hits"  color="#06b6d4"  />
          </div>

          {/* Token usage area chart */}
          <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold" style={{ color:'var(--text-2)' }}>Token Usage & Cost (24h)</span>
              <div className="flex gap-3 text-xs">
                <span style={{ color:'#60a5fa' }}>● Input tokens</span>
                <span style={{ color:'#a78bfa' }}>● Output tokens</span>
                <span style={{ color:'#4ade80' }}>● Cost ($)</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={HOURLY} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <defs>
                  {[['in','#60a5fa'],['out','#a78bfa']].map(([k,c]) => (
                    <linearGradient key={k} id={`ag-${k}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={0.3}/>
                      <stop offset="100%" stopColor={c} stopOpacity={0}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="2 2" stroke="rgba(255,255,255,.04)" vertical={false}/>
                <XAxis dataKey="hour" tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false} interval={3}/>
                <YAxis tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                <Area type="monotone" dataKey="inTok"  stroke="#60a5fa" strokeWidth={1.5} fill="url(#ag-in)"  name="Input tokens"/>
                <Area type="monotone" dataKey="outTok" stroke="#a78bfa" strokeWidth={1.5} fill="url(#ag-out)" name="Output tokens"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Cost by model + requests by model */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Cost Distribution by Model</div>
              <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                <PieChart width={120} height={120}>
                  <Pie data={costPieData} cx={59} cy={59} innerRadius={36} outerRadius={54} dataKey="value" strokeWidth={0}>
                    {costPieData.map((d,i) => <Cell key={i} fill={d.color}/>)}
                  </Pie>
                </PieChart>
                <div className="flex-1 space-y-1.5">
                  {costPieData.map(d => (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <span style={{ width:8, height:8, borderRadius:2, background:d.color, display:'inline-block', flexShrink:0 }}/>
                      <span className="truncate flex-1" style={{ color:'var(--text-2)' }}>{d.name}</span>
                      <span className="mono font-bold" style={{ color:d.color }}>${d.value.toFixed(3)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Requests per Hour (24h)</div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={HOURLY} barSize={8} margin={{ top:0, right:0, left:-20, bottom:0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="rgba(255,255,255,.04)" vertical={false}/>
                  <XAxis dataKey="hour" tick={{ fill:'#64748b', fontSize:8 }} axisLine={false} tickLine={false} interval={5}/>
                  <YAxis tick={{ fill:'#64748b', fontSize:8 }} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                  <Bar dataKey="requests" fill="#60a5fa80" radius={[2,2,0,0]} name="Requests"/>
                  <Bar dataKey="errors"   fill="#ef444480" radius={[2,2,0,0]} name="Errors"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Avg latency trend */}
          <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
            <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Avg Response Latency (24h)</div>
            <ResponsiveContainer width="100%" height={100}>
              <LineChart data={HOURLY} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="rgba(255,255,255,.04)" vertical={false}/>
                <XAxis dataKey="hour" tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false} interval={3}/>
                <YAxis tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}
                  formatter={v => [`${v}ms`, 'Avg Latency']}/>
                <ReferenceLine y={3000} stroke="#f59e0b" strokeDasharray="4 2" label={{ value:'3s threshold', fill:'#f59e0b', fontSize:9, position:'right' }}/>
                <Line type="monotone" dataKey="avgLat" stroke="#fbbf24" strokeWidth={2} dot={false} name="Avg Latency (ms)"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Traces ─────────────────────────────────────────────────────────────── */}
      {tab === 'Traces' && (
        <div className="flex-1 overflow-y-auto flex flex-col">
          {/* Filter bar */}
          <div className="px-5 py-2.5 shrink-0 flex items-center gap-2 flex-wrap"
            style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
            <div className="flex items-center gap-1 rounded px-2" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
              <Search size={11} style={{ color:'var(--text-3)' }}/>
              <input value={traceSearch} onChange={e => setSearch(e.target.value)} placeholder="Search trace ID, agent, task…"
                className="bg-transparent outline-none text-xs py-1 w-44" style={{ color:'var(--text)' }}/>
            </div>
            <div className="flex gap-1">
              {['ALL',...MODEL_NAMES].map(m => {
                const meta = MODELS[m];
                return (
                  <button key={m} onClick={() => setModelFilter(m)}
                    className="px-2 py-1 rounded text-xs"
                    style={{ background:modelFilter===m ? `${meta?.color || '#3b82f6'}20` : 'var(--bg-tertiary)', color:modelFilter===m ? (meta?.color||'#60a5fa') : 'var(--text-3)', border:`1px solid ${modelFilter===m ? (meta?.color||'#3b82f6')+'40' : 'var(--border)'}`, cursor:'pointer' }}>
                    {m === 'ALL' ? 'All Models' : m}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1">
              {['ALL','success','error','timeout'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className="px-2 py-1 rounded text-xs"
                  style={{ background:statusFilter===s?'rgba(59,130,246,.2)':'var(--bg-tertiary)', color:statusFilter===s?'#60a5fa':'var(--text-3)', border:`1px solid ${statusFilter===s?'rgba(59,130,246,.4)':'var(--border)'}`, cursor:'pointer' }}>
                  {s}
                </button>
              ))}
            </div>
            <span className="ml-auto text-xs" style={{ color:'var(--text-3)' }}>{filteredTraces.length} traces</span>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead style={{ position:'sticky', top:0, zIndex:10 }}>
                <tr style={{ background:'var(--bg-tertiary)' }}>
                  {['Trace ID','Model','Agent','Task','Tokens','Cost','Latency','Safety','Status','Feedback'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.7px', color:'var(--text-3)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTraces.slice(0, 100).map((t, i) => {
                  const m = MODELS[t.model];
                  const tc = toxColor(t.toxicity);
                  const gc = scoreColor(t.groundedness);
                  return (
                    <tr key={t.id}
                      onClick={() => setSelTrace(t)}
                      className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                      style={{ borderBottom:'1px solid rgba(255,255,255,.04)', background: i%2?'rgba(255,255,255,.01)':'transparent' }}>
                      <td style={{ padding:'9px 12px' }}>
                        <span className="mono text-xs font-bold" style={{ color:'var(--text-3)' }}>{t.id}</span>
                        <div className="text-xs" style={{ color:'var(--text-3)', fontSize:9 }}>{timeAgo(t.timestamp)}</div>
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        <div className="flex items-center gap-1.5">
                          <span>{m.icon}</span>
                          <div>
                            <div className="text-xs font-medium" style={{ color:m.color }}>{t.model}</div>
                            <div style={{ fontSize:9, color:'var(--text-3)' }}>{m.provider}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        <span className="text-xs" style={{ color:'var(--text-2)' }}>{t.agent}</span>
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        <span style={{ fontSize:10, padding:'1px 7px', borderRadius:20, background:'rgba(99,102,241,.15)', color:'#818cf8', border:'1px solid rgba(99,102,241,.3)' }}>{t.taskType}</span>
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        <div className="text-xs mono font-bold" style={{ color:'#a78bfa' }}>{fmtK(t.totalTokens)}</div>
                        <div style={{ fontSize:9, color:'var(--text-3)' }}>{fmtK(t.inTokens)} in / {fmtK(t.outTokens)} out</div>
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        <span className="mono text-xs font-bold" style={{ color:'#4ade80' }}>{fmtCost(t.cost)}</span>
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        <span className="mono text-xs" style={{ color:t.latencyMs>4000?'#f87171':t.latencyMs>2000?'#fbbf24':'var(--text-2)' }}>{fmtMs(t.latencyMs)}</span>
                        {t.cached && <span style={{ marginLeft:4, fontSize:8, color:'#60a5fa' }}>●cache</span>}
                      </td>
                      <td style={{ padding:'9px 12px' }}>
                        <div className="flex flex-col gap-0.5">
                          <div style={{ fontSize:9 }}>
                            <span style={{ color:'var(--text-3)' }}>G:</span>
                            <span style={{ color:gc, fontWeight:700 }}> {(t.groundedness*100).toFixed(0)}%</span>
                          </div>
                          <div style={{ fontSize:9 }}>
                            <span style={{ color:'var(--text-3)' }}>T:</span>
                            <span style={{ color:tc, fontWeight:700 }}> {(t.toxicity*100).toFixed(1)}%</span>
                          </div>
                          {t.promptInjection && <span style={{ fontSize:8, color:'#f87171', fontWeight:700 }}>⚠ INJ</span>}
                        </div>
                      </td>
                      <td style={{ padding:'9px 12px' }}><StatusBadge s={t.status}/></td>
                      <td style={{ padding:'9px 12px' }}>
                        {t.feedback === 'positive' && <ThumbsUp  size={12} style={{ color:'#4ade80' }}/>}
                        {t.feedback === 'negative' && <ThumbsDown size={12} style={{ color:'#f87171' }}/>}
                        {!t.feedback && <span style={{ fontSize:9, color:'var(--text-3)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Models ─────────────────────────────────────────────────────────────── */}
      {tab === 'Models' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Comparison table */}
          <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
            <div className="px-4 py-3" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
              <span className="text-xs font-semibold" style={{ color:'var(--text-2)' }}>Model Performance Comparison</span>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'var(--bg-tertiary)' }}>
                  {['Model','Provider','Requests','Avg Tokens','Total Cost','Avg Latency','P95 Latency','Err Rate','Avg Toxicity','Price / 1M'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.7px', color:'var(--text-3)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODEL_STATS.map((m, i) => (<React.Fragment key={m.model}>
                  <tr
                    onClick={() => setExpandModel(expandModel === m.model ? null : m.model)}
                    className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                    style={{ borderBottom: expandModel===m.model ? 'none' : '1px solid rgba(255,255,255,.04)', background:i%2?'rgba(255,255,255,.01)':'transparent' }}>
                    <td style={{ padding:'10px 12px' }}>
                      <div className="flex items-center gap-2">
                        <span>{m.icon}</span>
                        <span className="font-semibold text-xs" style={{ color:m.color }}>{m.model}</span>
                      </div>
                    </td>
                    <td style={{ padding:'10px 12px' }}><span style={{ fontSize:10, color:'var(--text-3)' }}>{m.provider}</span></td>
                    <td style={{ padding:'10px 12px' }}><span className="mono font-bold" style={{ color:'#60a5fa' }}>{m.requests}</span></td>
                    <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:'#a78bfa' }}>{m.requests?Math.round(m.totalTokens/m.requests):0}</span></td>
                    <td style={{ padding:'10px 12px' }}><span className="mono font-bold" style={{ color:'#4ade80' }}>${m.cost.toFixed(3)}</span></td>
                    <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:m.avgLat>3000?'#f87171':m.avgLat>1500?'#fbbf24':'var(--text-2)' }}>{fmtMs(m.avgLat)}</span></td>
                    <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:'var(--text-3)' }}>{fmtMs(m.p95)}</span></td>
                    <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:m.errRate>3?'#f87171':m.errRate>1?'#fbbf24':'#4ade80' }}>{m.errRate}%</span></td>
                    <td style={{ padding:'10px 12px' }}><span className="mono" style={{ color:toxColor(m.avgTox) }}>{(m.avgTox*100).toFixed(2)}%</span></td>
                    <td style={{ padding:'10px 12px' }}>
                      <div style={{ fontSize:10, color:'var(--text-3)' }}>
                        <div>in: <span style={{ color:m.color, fontWeight:700 }}>${m.inPer1M.toFixed(2)}</span></div>
                        <div>out: <span style={{ color:m.color, fontWeight:700 }}>${m.outPer1M.toFixed(2)}</span></div>
                      </div>
                    </td>
                  </tr>
                  {expandModel === m.model && (
                    <tr style={{ background:'var(--bg-primary)' }}>
                      <td colSpan={10} style={{ padding:0, borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                        <div className="p-4 flex flex-col gap-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span>{m.icon}</span>
                            <span className="font-bold text-sm" style={{ color:m.color }}>{m.model}</span>
                            <span className="text-xs" style={{ color:'var(--text-3)' }}>· {m.requests} requests · ${m.cost.toFixed(3)} total</span>
                          </div>
                          {/* Score bars */}
                          <div className="grid grid-cols-4 gap-3">
                            {[
                              { l:'Avg Groundedness', v:ALL_TRACES.filter(t=>t.model===m.model).reduce((s,t)=>s+t.groundedness,0)/Math.max(1,m.requests) },
                              { l:'Avg Relevance',    v:ALL_TRACES.filter(t=>t.model===m.model).reduce((s,t)=>s+t.relevance,0)/Math.max(1,m.requests) },
                              { l:'Avg Faithfulness', v:ALL_TRACES.filter(t=>t.model===m.model).reduce((s,t)=>s+t.faithfulness,0)/Math.max(1,m.requests) },
                              { l:'Avg Toxicity',     v:ALL_TRACES.filter(t=>t.model===m.model).reduce((s,t)=>s+t.toxicity,0)/Math.max(1,m.requests), inv:true },
                            ].map(sc => {
                              const disp = sc.inv ? 1-sc.v : sc.v;
                              const c = sc.inv ? toxColor(sc.v) : scoreColor(sc.v);
                              return (
                                <div key={sc.l} className="rounded-lg p-3" style={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)' }}>
                                  <div className="flex justify-between text-xs mb-1.5">
                                    <span style={{ color:'var(--text-3)' }}>{sc.l}</span>
                                    <span className="mono font-bold" style={{ color:c }}>{(disp*100).toFixed(1)}%</span>
                                  </div>
                                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background:'var(--bg-primary)' }}>
                                    <div style={{ width:`${disp*100}%`, height:'100%', background:c, borderRadius:99 }}/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Recent traces for this model */}
                          <div className="text-xs font-semibold mb-1" style={{ color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.6px' }}>Recent Traces</div>
                          <div className="rounded-lg overflow-hidden" style={{ border:'1px solid var(--border)' }}>
                            {ALL_TRACES.filter(t => t.model === m.model).slice(0,5).map((t, ti) => (
                              <div key={t.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03]"
                                style={{ borderBottom:ti<4?'1px solid rgba(255,255,255,.04)':'none', fontSize:11 }}
                                onClick={e => { e.stopPropagation(); }}>
                                <span className="mono" style={{ color:'var(--text-3)', fontSize:9 }}>{t.id}</span>
                                <span style={{ color:'var(--text-3)' }}>{t.agent}</span>
                                <span style={{ fontSize:9, padding:'1px 6px', borderRadius:20, background:'rgba(99,102,241,.15)', color:'#818cf8' }}>{t.taskType}</span>
                                <span className="mono ml-auto" style={{ color:'var(--text-3)' }}>{fmtMs(t.latencyMs)}</span>
                                <StatusBadge s={t.status}/>
                                <span style={{ color:'var(--text-3)', fontSize:9 }}>{timeAgo(t.timestamp)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>))}
              </tbody>
            </table>
          </div>

          {/* Cost vs Latency scatter-like bar */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Total Cost by Model</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={MODEL_STATS} layout="vertical" barSize={16} margin={{ top:0, right:10, left:30, bottom:0 }}>
                  <XAxis type="number" tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v.toFixed(2)}`}/>
                  <YAxis type="category" dataKey="model" tick={{ fill:'#94a3b8', fontSize:9 }} axisLine={false} tickLine={false} width={110}/>
                  <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }} formatter={v=>[`$${v.toFixed(4)}`,'Cost']}/>
                  <Bar dataKey="cost" radius={[0,4,4,0]}>
                    {MODEL_STATS.map((m,i) => <Cell key={i} fill={m.color}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Avg Latency by Model (ms)</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={MODEL_STATS} layout="vertical" barSize={16} margin={{ top:0, right:10, left:30, bottom:0 }}>
                  <XAxis type="number" tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                  <YAxis type="category" dataKey="model" tick={{ fill:'#94a3b8', fontSize:9 }} axisLine={false} tickLine={false} width={110}/>
                  <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }} formatter={v=>[`${v}ms`,'Avg Latency']}/>
                  <ReferenceLine x={3000} stroke="#f59e0b" strokeDasharray="3 2"/>
                  <Bar dataKey="avgLat" radius={[0,4,4,0]}>
                    {MODEL_STATS.map((m,i) => <Cell key={i} fill={m.avgLat>3000?'#f87171':m.color}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ── Safety ─────────────────────────────────────────────────────────────── */}
      {tab === 'Safety' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Safety KPIs */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { icon:ShieldAlert,  label:'Toxic Responses',      value:TOXIC_COUNT,  sub:`${((TOXIC_COUNT/TOTAL)*100).toFixed(1)}% of requests`, color:'#ef4444' },
              { icon:Lock,         label:'Prompt Injections',     value:PI_COUNT,     sub:`${((PI_COUNT/TOTAL)*100).toFixed(1)}% detection rate`,  color:'#f97316' },
              { icon:Fingerprint,  label:'PII Detected',          value:PII_COUNT,    sub:'masked before storage',                                  color:'#f59e0b' },
              { icon:FlaskConical, label:'Low Groundedness',      value:LOW_GROUND,   sub:'below 75% threshold',                                    color:'#8b5cf6' },
            ].map(k => {
              const Icon = k.icon;
              return (
                <div key={k.label} className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderTop:`3px solid ${k.color}` }}>
                  <div className="flex items-center gap-1.5 text-xs mb-2" style={{ color:'var(--text-3)' }}>
                    <Icon size={11} style={{ color:k.color }}/>{k.label}
                  </div>
                  <div className="mono text-2xl font-bold" style={{ color:k.color }}>{k.value}</div>
                  <div className="text-xs mt-1" style={{ color:'var(--text-3)' }}>{k.sub}</div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Toxicity histogram */}
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Toxicity Score Distribution</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={toxBuckets} barSize={28} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                  <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="rgba(255,255,255,.04)"/>
                  <XAxis dataKey="range" tick={{ fill:'#64748b', fontSize:10 }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                  <Bar dataKey="count" name="Requests" radius={[3,3,0,0]}>
                    {toxBuckets.map((_, i) => <Cell key={i} fill={i<2?'#22c55e':i<3?'#f59e0b':'#ef4444'}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="text-xs mt-2 text-center" style={{ color:'var(--text-3)' }}>
                {((TOXIC_COUNT/TOTAL)*100).toFixed(1)}% of requests exceeded 10% toxicity threshold
              </div>
            </div>

            {/* Groundedness histogram */}
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Groundedness Score Distribution</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={groundBuckets} barSize={28} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                  <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="rgba(255,255,255,.04)"/>
                  <XAxis dataKey="range" tick={{ fill:'#64748b', fontSize:10 }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                  <Bar dataKey="count" name="Requests" radius={[3,3,0,0]}>
                    {groundBuckets.map((_, i) => <Cell key={i} fill={i===0?'#ef4444':i===1?'#f59e0b':i===2?'#fbbf24':'#22c55e'}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="text-xs mt-2 text-center" style={{ color:'var(--text-3)' }}>
                {((LOW_GROUND/TOTAL)*100).toFixed(1)}% of requests below 75% groundedness threshold
              </div>
            </div>
          </div>

          {/* Safety by model */}
          <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
            <div className="px-4 py-3" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
              <span className="text-xs font-semibold" style={{ color:'var(--text-2)' }}>Safety Metrics by Model</span>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'var(--bg-tertiary)' }}>
                  {['Model','Avg Toxicity','Avg Groundedness','Avg Faithfulness','Avg Relevance','PI Attempts','PII Events'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.7px', color:'var(--text-3)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODEL_NAMES.map((mn, i) => {
                  const traces = ALL_TRACES.filter(t => t.model === mn);
                  const n = traces.length || 1;
                  const avgTox  = traces.reduce((s,t) => s+t.toxicity,0)/n;
                  const avgGnd  = traces.reduce((s,t) => s+t.groundedness,0)/n;
                  const avgFth  = traces.reduce((s,t) => s+t.faithfulness,0)/n;
                  const avgRel  = traces.reduce((s,t) => s+t.relevance,0)/n;
                  const piCnt   = traces.filter(t=>t.promptInjection).length;
                  const piiCnt  = traces.filter(t=>t.piiDetected).length;
                  return (
                    <tr key={mn} style={{ borderBottom:'1px solid rgba(255,255,255,.04)', background:i%2?'rgba(255,255,255,.01)':'transparent' }}>
                      <td style={{ padding:'10px 12px' }}>
                        <div className="flex items-center gap-2">
                          <span>{MODELS[mn].icon}</span>
                          <span className="text-xs font-semibold" style={{ color:MODELS[mn].color }}>{mn}</span>
                        </div>
                      </td>
                      <td style={{ padding:'10px 12px' }}><ScorePill value={avgTox} colorFn={toxColor} labelFn={toxLabel} size="xs"/></td>
                      <td style={{ padding:'10px 12px' }}><ScorePill value={avgGnd} colorFn={scoreColor} labelFn={scoreLabel} size="xs"/></td>
                      <td style={{ padding:'10px 12px' }}><ScorePill value={avgFth} colorFn={scoreColor} labelFn={scoreLabel} size="xs"/></td>
                      <td style={{ padding:'10px 12px' }}><ScorePill value={avgRel} colorFn={scoreColor} labelFn={scoreLabel} size="xs"/></td>
                      <td style={{ padding:'10px 12px' }}><span className="mono text-xs" style={{ color:piCnt>0?'#f87171':'var(--text-3)' }}>{piCnt}</span></td>
                      <td style={{ padding:'10px 12px' }}><span className="mono text-xs" style={{ color:piiCnt>0?'#fbbf24':'var(--text-3)' }}>{piiCnt}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Recent flagged */}
          <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
            <div className="px-4 py-3" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
              <span className="text-xs font-semibold" style={{ color:'#f87171' }}>⚠ Recently Flagged Responses</span>
            </div>
            {ALL_TRACES.filter(t => t.toxicity > 0.1 || t.promptInjection || t.piiDetected).slice(0,8).map((t, i) => {
              const m = MODELS[t.model];
              return (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
                  style={{ borderBottom:'1px solid rgba(255,255,255,.04)' }}
                  onClick={() => setSelTrace(t)}>
                  <span>{m.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="mono text-xs font-bold" style={{ color:'var(--text-3)' }}>{t.id}</span>
                      <span style={{ fontSize:9, color:m.color }}>{t.model}</span>
                      {t.promptInjection && <span style={{ fontSize:9, fontWeight:800, padding:'1px 6px', borderRadius:20, background:'rgba(239,68,68,.15)', color:'#f87171', border:'1px solid rgba(239,68,68,.3)' }}>INJECTION</span>}
                      {t.piiDetected     && <span style={{ fontSize:9, fontWeight:800, padding:'1px 6px', borderRadius:20, background:'rgba(245,158,11,.15)', color:'#fbbf24', border:'1px solid rgba(245,158,11,.3)' }}>PII</span>}
                      {t.toxicity > 0.1  && <span style={{ fontSize:9, fontWeight:800, padding:'1px 6px', borderRadius:20, background:'rgba(239,68,68,.15)', color:'#f87171', border:'1px solid rgba(239,68,68,.3)' }}>TOXIC {(t.toxicity*100).toFixed(1)}%</span>}
                    </div>
                    <div className="text-xs truncate" style={{ color:'var(--text-3)' }}>{t.prompt}</div>
                  </div>
                  <span className="text-xs shrink-0" style={{ color:'var(--text-3)' }}>{timeAgo(t.timestamp)}</span>
                  <ChevronRight size={12} style={{ color:'var(--text-3)', flexShrink:0 }}/>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Feedback ──────────────────────────────────────────────────────────── */}
      {tab === 'Feedback' && (
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Feedback KPIs */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { icon:MessageSquare, label:'Total Feedback',    value:FEEDBACK_TRACES.length,                 sub:`${((FEEDBACK_TRACES.length/TOTAL)*100).toFixed(1)}% response rate`, color:'#60a5fa' },
              { icon:ThumbsUp,      label:'Positive',          value:POS_COUNT,                              sub:`${((POS_COUNT/FEEDBACK_TRACES.length)*100).toFixed(1)}% satisfaction`,  color:'#22c55e' },
              { icon:ThumbsDown,    label:'Negative',          value:NEG_COUNT,                              sub:`${((NEG_COUNT/FEEDBACK_TRACES.length)*100).toFixed(1)}% dissatisfied`,  color:'#ef4444' },
              { icon:Star,          label:'Avg Rating',        value:(FEEDBACK_TRACES.filter(t=>t.rating).reduce((s,t)=>s+t.rating,0)/Math.max(1,FEEDBACK_TRACES.filter(t=>t.rating).length)).toFixed(1)+'/5',  sub:`${FEEDBACK_TRACES.filter(t=>t.rating).length} rated responses`, color:'#f59e0b' },
            ].map(k => {
              const Icon = k.icon;
              return (
                <div key={k.label} className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderTop:`3px solid ${k.color}` }}>
                  <div className="flex items-center gap-1.5 text-xs mb-2" style={{ color:'var(--text-3)' }}>
                    <Icon size={11} style={{ color:k.color }}/>{k.label}
                  </div>
                  <div className="mono text-2xl font-bold" style={{ color:k.color }}>{k.value}</div>
                  <div className="text-xs mt-1" style={{ color:'var(--text-3)' }}>{k.sub}</div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Feedback trend */}
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Feedback Trend (7 days)</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={feedbackTrend} barCategoryGap="30%" margin={{ top:4, right:4, left:-20, bottom:0 }}>
                  <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="rgba(255,255,255,.04)"/>
                  <XAxis dataKey="day" tick={{ fill:'#64748b', fontSize:10 }} axisLine={false} tickLine={false}/>
                  <YAxis tick={{ fill:'#64748b', fontSize:9 }} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{ background:'var(--bg-tertiary)', border:'1px solid var(--border)', fontSize:11 }}/>
                  <Legend wrapperStyle={{ fontSize:10 }}/>
                  <Bar dataKey="pos" name="Positive" fill="#22c55e80" radius={[3,3,0,0]}/>
                  <Bar dataKey="neg" name="Negative" fill="#ef444480" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Satisfaction by model */}
            <div className="rounded-xl p-4" style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)' }}>
              <div className="text-xs font-semibold mb-3" style={{ color:'var(--text-2)' }}>Satisfaction Rate by Model</div>
              <div className="space-y-3">
                {MODEL_NAMES.map(mn => {
                  const modelFeedback = FEEDBACK_TRACES.filter(t => t.model === mn);
                  const pos = modelFeedback.filter(t => t.feedback === 'positive').length;
                  const total = modelFeedback.length || 1;
                  const pct = Math.round((pos/total)*100);
                  const c = pct>=80?'#22c55e':pct>=60?'#f59e0b':'#ef4444';
                  return (
                    <div key={mn}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1.5">
                          <span>{MODELS[mn].icon}</span>
                          <span style={{ color:'var(--text-2)' }}>{mn}</span>
                        </span>
                        <span style={{ color:c, fontWeight:700, fontFamily:'monospace' }}>{pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background:'var(--bg-primary)' }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:c, borderRadius:99 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent feedback comments */}
          <div className="rounded-xl overflow-hidden" style={{ border:'1px solid var(--border)' }}>
            <div className="px-4 py-3" style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
              <span className="text-xs font-semibold" style={{ color:'var(--text-2)' }}>Recent User Feedback</span>
            </div>
            {FEEDBACK_COMMENTS.map((fb, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3"
                style={{ borderBottom:i<FEEDBACK_COMMENTS.length-1?'1px solid rgba(255,255,255,.04)':'none' }}>
                {fb.type==='positive'
                  ? <ThumbsUp  size={13} style={{ color:'#4ade80', marginTop:1, flexShrink:0 }}/>
                  : <ThumbsDown size={13} style={{ color:'#f87171', marginTop:1, flexShrink:0 }}/>}
                <p className="text-sm leading-relaxed flex-1" style={{ color:'var(--text-2)' }}>{fb.text}</p>
                <span style={{ fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:20, background:fb.type==='positive'?'rgba(34,197,94,.15)':'rgba(239,68,68,.15)', color:fb.type==='positive'?'#4ade80':'#f87171', border:`1px solid ${fb.type==='positive'?'rgba(34,197,94,.3)':'rgba(239,68,68,.3)'}`, flexShrink:0 }}>
                  {fb.type.toUpperCase()}
                </span>
              </div>
            ))}
          </div>

          {/* Feedback loop insight */}
          <div className="rounded-xl p-4" style={{ background:'rgba(139,92,246,.06)', border:'1px solid rgba(139,92,246,.2)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Zap size={13} style={{ color:'#c4b5fd' }}/>
              <span className="text-xs font-semibold" style={{ color:'#c4b5fd' }}>AI Feedback Loop Insights</span>
            </div>
            <ul className="space-y-1.5 text-xs" style={{ color:'var(--text-2)' }}>
              <li>• <strong>gpt-4o</strong> has highest satisfaction rate ({Math.round(((FEEDBACK_TRACES.filter(t=>t.model==='gpt-4o'&&t.feedback==='positive').length)/Math.max(1,FEEDBACK_TRACES.filter(t=>t.model==='gpt-4o').length))*100)}%) but highest cost per request</li>
              <li>• Negative feedback correlates with <strong>groundedness &lt; 75%</strong> in 68% of cases — improve RAG retrieval pipeline</li>
              <li>• <strong>code-gen</strong> tasks have 2.1× more negative feedback than <strong>summarization</strong> tasks</li>
              <li>• Responses with latency &gt; 4s receive <strong>22% more negative feedback</strong> — optimize high-latency paths</li>
              <li>• Enabling prompt caching could reduce cost by ~{((ALL_TRACES.filter(t=>!t.cached).length * 0.15 / ALL_TRACES.length)*100).toFixed(0)}% based on current prompt reuse patterns</li>
            </ul>
          </div>
        </div>
      )}

      {/* Trace detail slide panel */}
      {selTrace && <TracePanel trace={selTrace} onClose={() => setSelTrace(null)} />}
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
      style={{ background:`${color}12`, border:`1px solid ${color}25` }}>
      <span style={{ color:'var(--text-3)' }}>{label}</span>
      <span className="font-bold mono" style={{ color }}>{value}</span>
    </div>
  );
}
